import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ImageDown, PencilLine, Share2, Trophy } from "lucide-react";
import { api, errMessage } from "../../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const THEMES = [
  { id: "classic", label: "Classic", swatch: "#AB4B2B" },
  { id: "forest", label: "Forest", swatch: "#14532D" },
  { id: "sunset", label: "Sunset", swatch: "#C2410C" },
];

// Dashboard cheer block: every application the family marked ACCEPTED,
// with proud notes and a live-preview shareable wins card in three themes.
export const FamilyWins = () => {
  const [wins, setWins] = useState(null);
  const [theme, setTheme] = useState("classic");
  const [ver, setVer] = useState(0);

  const load = () =>
    api.get("/opportunities/outcomes")
      .then(({ data }) => setWins(data.outcomes.filter((o) => o.outcome === "accepted")))
      .catch(() => setWins([]));
  useEffect(() => { load(); }, []);

  const editNote = async (o) => {
    const val = window.prompt(
      "A short proud note for this win — it shows on your shareable wins card (max 120 chars). Leave empty to clear.",
      o.win_note || "");
    if (val == null) return;
    try {
      await api.post(`/opportunities/${o.opportunity_id}/action`,
        { win_note: val.trim().slice(0, 120) });
      await load();
      setVer((v) => v + 1);
      toast.success(val.trim() ? "Note added to your wins card" : "Note cleared");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (!wins?.length) return null;
  const cardUrl = `${API_BASE}/api/opportunities/wins-card.png?theme=${theme}&v=${ver}`;

  const getCardBlob = async () => {
    const res = await fetch(cardUrl, { credentials: "include" });
    if (!res.ok) throw new Error("card fetch failed");
    return res.blob();
  };

  const copyImage = async () => {
    try {
      const blob = await getCardBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Wins card copied — paste it into any chat");
    } catch {
      toast.error("Copying images isn't supported in this browser — use Share or the download link");
    }
  };

  const shareImage = async () => {
    try {
      const blob = await getCardBlob();
      const file = new File([blob], "family-wins.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Our family's wins" });
      } else {
        await copyImage();
      }
    } catch (e) {
      if (e?.name !== "AbortError")
        toast.error("Sharing isn't supported in this browser — use Copy or the download link");
    }
  };

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-2" data-testid="family-wins">
      <p className="text-sm font-bold flex items-center gap-1.5 text-emerald-800">
        <Trophy className="h-4 w-4" aria-hidden="true" /> Your family's wins this season
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-[11px] font-bold px-2 py-0.5"
          data-testid="family-wins-count">
          <Trophy className="h-3 w-3" aria-hidden="true" />
          {wins.length} {wins.length === 1 ? "win" : "wins"}
        </span>
      </p>
      {wins.length >= 3 && (
        <p className="rounded-xl bg-emerald-700 text-white text-xs font-bold px-3 py-2"
          data-testid="win-streak-banner">
          🎉 {wins.length >= 10
            ? "TEN family wins — an incredible season for this family!"
            : wins.length >= 5
              ? "Five family wins — this family is on a roll!"
              : "Three family wins — a streak worth celebrating!"}
        </p>
      )}
      {wins.slice(0, 5).map((o) => (
        <div key={o.opportunity_id} className="text-xs" data-testid={`family-win-${o.opportunity_id}`}>
          <span className="font-semibold">{o.title}</span>
          <span className="text-muted-foreground">
            {" "}— {o.organization}
            {o.outcome_at ? ` · accepted ${new Date(o.outcome_at).toLocaleDateString()}` : ""}
          </span>
          <button type="button" onClick={() => editNote(o)}
            className="ml-2 inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline"
            data-testid={`family-win-note-btn-${o.opportunity_id}`}>
            <PencilLine className="h-3 w-3" aria-hidden="true" />
            {o.win_note ? "Edit note" : "Add note"}
          </button>
          {o.win_note && (
            <p className="italic text-emerald-800/80 mt-0.5" data-testid={`family-win-note-${o.opportunity_id}`}>
              “{o.win_note}”
            </p>
          )}
        </div>
      ))}
      <img src={cardUrl} alt="Preview of your shareable family wins card"
        className="mt-1 w-full max-w-xs rounded-xl border border-emerald-200 shadow-sm"
        data-testid="family-wins-preview" />
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/opportunities" className="text-xs font-semibold text-emerald-700 hover:underline inline-block"
          data-testid="family-wins-link">
          Open the Opportunity Navigator →
        </Link>
        <a href={cardUrl} download="family-wins.png"
          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
          data-testid="family-wins-image">
          <ImageDown className="h-3.5 w-3.5" aria-hidden="true" /> Download
        </a>
        <button type="button" onClick={shareImage}
          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
          data-testid="family-wins-share">
          <Share2 className="h-3.5 w-3.5" aria-hidden="true" /> Share
        </button>
        <button type="button" onClick={copyImage}
          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
          data-testid="family-wins-copy">
          <Copy className="h-3.5 w-3.5" aria-hidden="true" /> Copy image
        </button>
        <span className="inline-flex items-center gap-1" data-testid="family-wins-themes">
          {THEMES.map((t) => (
            <button key={t.id} type="button" onClick={() => setTheme(t.id)} title={`${t.label} theme`}
              aria-label={`${t.label} card theme`} aria-pressed={theme === t.id}
              className={`h-4 w-4 rounded-full border transition-transform hover:scale-110 ${
                theme === t.id ? "ring-2 ring-offset-1 ring-emerald-600 border-transparent" : "border-black/10"}`}
              style={{ backgroundColor: t.swatch }}
              data-testid={`family-wins-theme-${t.id}`} />
          ))}
        </span>
      </div>
    </div>
  );
};
